<script setup lang="ts">
/**
 * One-step account creation around a purchase on the ad landing page.
 *
 * The visitor has already seen their finished CV, so this asks for the minimum:
 * e-mail and password. Before a payment, a project that requires e-mail
 * confirmation gets its round trip and the purchase resumes from /auth/callback.
 *
 * After a payment (`claim` with the token of a paid pack) it does not: the buyer
 * is standing in front of something they own, so the account is created already
 * confirmed by the server and they are signed in on the spot. The token is what
 * makes that safe — see server/api/guest-export/create-account.post.ts.
 */
import { MARKETING_CONSENT_METADATA } from '~/composables/useMarketingConsent'

const props = defineProps<{
  modelValue: boolean
  /** Shown above the form, e.g. "Job Search Pack – $29". */
  offerLabel?: string
  /**
   * The purchase is already paid for and this account is what it gets handed to.
   * Same form, different promise: nothing is being sold here any more.
   */
  claim?: boolean
  /** The paid pack this account is for; skips the confirmation e-mail. */
  claimToken?: string
  /**
   * What the account is for. A pack hands over the web CV and 30 days of
   * Premium; `edit` is the single-PDF buyer who came to change something and
   * download the new version, so promising them the pack would be a lie.
   * `prep` is an interview prep bought without an account, saved onto one.
   */
  claimKind?: 'pack' | 'edit' | 'prep'
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  /** Signup succeeded and a session exists: the caller can open checkout now. */
  (e: 'signed-in'): void
  /** Account created but e-mail confirmation is pending. */
  (e: 'confirm-email', email: string): void
}>()

const { t } = useI18n()

/** `claimTitle`/`claimSubtitle`/`claimSubmit`, or their single-PDF or interview prep wording. */
const claimKey = computed(() => (props.claimKind === 'edit' || props.claimKind === 'prep' ? props.claimKind : 'claim'))
const { register } = useUser()
const client = useSupabaseClient()

const email = ref('')
const password = ref('')
const agreeToTerms = ref(false)
/** The optional marketing opt-in; separate from the terms and never pre-ticked. */
const marketingConsent = ref(false)
const { consentText: marketingConsentText, setMarketingConsent, applyPendingRegistrationConsent } = useMarketingConsent()

/** Signed in already: the opt-in applies now. Never blocks the sign-up. */
async function applyMarketingConsent() {
  if (!marketingConsent.value) return
  await setMarketingConsent(true, 'registration').catch(err => console.error('[quick-signup] opt-in failed:', err))
}
const isLoading = ref(false)
const error = ref('')

function close() {
  if (isLoading.value) return
  emit('update:modelValue', false)
}

/** Signs in with what was just typed; the caller decides what that unlocks. */
async function signIn(): Promise<boolean> {
  const { error: loginError } = await client.auth.signInWithPassword({
    email: email.value,
    password: password.value,
  })
  return !loginError
}

/**
 * Paid pack: the server creates the account as confirmed, so there is no e-mail
 * to wait for. An address that already has an account simply signs in instead.
 */
async function submitClaim() {
  try {
    const response = await $fetch<{ success: boolean; exists: boolean }>('/api/guest-export/create-account', {
      method: 'POST',
      body: { token: props.claimToken, email: email.value, password: password.value },
    })

    if (!response.success && response.exists && !(await signIn())) {
      error.value = t('landing.signup.errors.existingAccount')
      return
    }

    if (response.success && !(await signIn())) {
      error.value = t('auth.register.errors.general')
      return
    }

    await applyMarketingConsent()
    emit('signed-in')
  } catch (err) {
    // The purchase is safe either way, so the copy points at the e-mailed link.
    console.error('Could not create the account for a paid pack:', err)
    error.value = t('landing.claim.error')
  }
}

async function submit() {
  error.value = ''

  if (password.value.length < 6) {
    error.value = t('auth.register.errors.passwordTooShort')
    return
  }
  if (!agreeToTerms.value) {
    error.value = t('auth.register.errors.termsRequired')
    return
  }

  isLoading.value = true
  try {
    if (props.claim && props.claimToken) {
      await submitClaim()
      return
    }

    const { data, error: registerError } = await register(
      email.value,
      password.value,
      undefined,
      marketingConsent.value ? { [MARKETING_CONSENT_METADATA]: true } : undefined,
    )

    if (registerError) {
      // Someone coming back to buy a second time already has an account: log them in instead.
      const message = registerError.message || ''
      if (/already registered|already exists|User already/i.test(message)) {
        if (!(await signIn())) {
          error.value = t('landing.signup.errors.existingAccount')
          return
        }
        await applyMarketingConsent()
        emit('signed-in')
        return
      }
      error.value = registerError.message.includes('For security purposes')
        ? registerError.message
        : t('auth.register.errors.general')
      return
    }

    if (data?.session) {
      // Signed up and in at once: apply (and clear) the opt-in from the metadata now.
      await applyPendingRegistrationConsent()
      emit('signed-in')
    } else {
      emit('confirm-email', email.value)
    }
  } catch (e) {
    console.error('Quick signup failed:', e)
    error.value = t('common.error')
  } finally {
    isLoading.value = false
  }
}

async function signUpWithGoogle() {
  isLoading.value = true
  error.value = ''
  try {
    await client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    })
  } catch (e) {
    console.error('Google sign-in error:', e)
    error.value = t('auth.login.errors.general')
    isLoading.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="props.modelValue"
      class="fixed inset-0 z-[1200] flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm"
      @click.self="close"
    >
      <!--
        Teleported out of the layout, so the panel re-opens the `.sofax` scope
        itself; otherwise the fields and buttons would lose the design system.
      -->
      <div class="sofax w-full max-w-md rounded-[0.625rem] bg-white p-6 shadow-lift sm:p-8 dark:bg-gray-800">
        <button
          type="button"
          class="float-right -mr-2 -mt-2 inline-flex size-9 items-center justify-center rounded-full text-ink hover:bg-surface dark:text-white dark:hover:bg-gray-700"
          :disabled="isLoading"
          :aria-label="t('common.close')"
          @click="close"
        >
          <i class="bi bi-x-lg" />
        </button>

        <h3 class="mb-2 !text-2xl">{{ claim ? t(`landing.signup.${claimKey}Title`) : t('landing.signup.title') }}</h3>
        <p class="mb-6 text-base">
          <template v-if="claim">{{ t(`landing.signup.${claimKey}Subtitle`) }}</template>
          <template v-else>
            {{ offerLabel ? t('landing.signup.subtitleOffer', { offer: offerLabel }) : t('landing.signup.subtitle') }}
          </template>
        </p>

        <!-- Google first: this sits in a checkout flow, so the fastest path comes first -->
        <SofaxGoogleButton
          :label="t('auth.login.continueWithGoogle')"
          :disabled="isLoading"
          @click="signUpWithGoogle"
        />

        <div class="my-5 flex items-center gap-3">
          <div class="h-px flex-1 bg-line dark:bg-gray-700" />
          <div class="text-sm text-muted dark:text-gray-400">{{ t('common.or') }}</div>
          <div class="h-px flex-1 bg-line dark:bg-gray-700" />
        </div>

        <form class="grid grid-cols-1 gap-y-5" @submit.prevent="submit">
          <SofaxInput
            v-model="email"
            type="email"
            required
            autocomplete="email"
            :label="t('auth.register.email')"
            :placeholder="t('auth.login.emailPlaceholder')"
            :disabled="isLoading"
          />
          <SofaxInput
            v-model="password"
            type="password"
            required
            autocomplete="new-password"
            :label="t('auth.register.password')"
            :placeholder="t('auth.register.passwordPlaceholder')"
            :disabled="isLoading"
          />

          <SofaxCheckbox v-model="agreeToTerms" :disabled="isLoading">
            {{ t('auth.register.agreeToTerms') }}
            <LocaleLink to="/terms" target="_blank" class="link">{{ t('auth.register.terms') }}</LocaleLink>
            {{ t('auth.register.and') }}
            <LocaleLink to="/privacy" target="_blank" class="link">{{ t('auth.register.privacy') }}</LocaleLink>
          </SofaxCheckbox>

          <SofaxCheckbox v-model="marketingConsent" :disabled="isLoading">
            {{ marketingConsentText }}
          </SofaxCheckbox>

          <SofaxNotice v-if="error" type="error">{{ error }}</SofaxNotice>

          <SofaxBtn
            :label="isLoading ? t('common.loading') : claim ? t(`landing.signup.${claimKey}Submit`) : t('landing.signup.submit')"
            variant="accent"
            type="submit"
            block
            :disabled="isLoading"
          />
        </form>

        <p class="mt-5 text-center text-sm">
          {{ t('landing.signup.hasAccount') }}
          <LocaleLink to="/login" class="link">{{ t('auth.register.signIn') }}</LocaleLink>
        </p>
      </div>
    </div>
  </Teleport>
</template>
